import React, { useEffect, useState, useCallback, useRef } from "react";
import { Typography, Button, Box, TextField } from "@mui/material";
import FocusedFlowPage from "components/FocusedFlowPage";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { usePatient } from "./PatientProvider";
import ParticipantConfirmationSummary from "../../../components/participant/ParticipantConfirmationSummary";
import {
  fetchPatientById,
  updatePatient,
  createEncounter,
  fetchAPI,
} from "utils/api";
import { v4 as uuidv4 } from "uuid";
import { CHECK_IN_STATUS } from "constants/checkInStatus";
import { ENCOUNTER_TYPE } from "constants/encounterType";
import {
  chicagoDateTimeLocalToIso,
  formatDateInAppTimeZone,
  formatLongDateInAppTimeZone,
  formatTimeInAppTimeZone,
  isoToChicagoDateTimeLocal,
  nowIso,
} from "utils/appDateTime";
import { formatDateUTC } from "utils/formatDateTime";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatScheduledAppointmentDateTime = (value) => {
  const dateText = formatLongDateInAppTimeZone(value);
  const timeText = formatTimeInAppTimeZone(value);
  if (!dateText || !timeText) return "";

  return `${dateText} at ${timeText}`;
};

const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
 
const formatBirthDate = (bd) => {
  return formatDateUTC(bd);
};

const compactInputSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 28,
    height: 28,
    fontSize: "16px",
    padding: 0,
  },
  "& .MuiInputBase-input": {
    py: 0,
    px: "8px",
    height: "28px",
    boxSizing: "border-box",
  },
};
 
// ── Component ─────────────────────────────────────────────────────────────────
 
const ConfirmPatientCheckInInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientData, setPatientData, updatePatientData } = usePatient();
 
  const [scheduledTime, setScheduledTime] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [signedStudies, setSignedStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: "",
    mobilePhoneNumber: "",
  });
  const [contactErrors, setContactErrors] = useState({});

  const checkInTimeSetRef = useRef(false);

  const locationUser = location.state?.user;
  const patientId = patientData?.patientId;
  const patientEmail = patientData?.email || "";
  const patientMobilePhoneNumber = patientData?.mobilePhoneNumber || "";
 
  // ── Fetch full patient record ───────────────────────────────────────────────
  useEffect(() => {
    const idToFetch = locationUser?.patientId || patientData?.patientId;
    if (!idToFetch) return;

    // Prevent multiple fetches
    if (patientData?.patientId === idToFetch && patientData?.email) {
      return;
    }

    const fetchFullPatient = async () => {
      try {
        console.log("🔍 ConfirmPatientInfo - Fetching full patient record for ID:", idToFetch);
        const fullPatient = await fetchPatientById(idToFetch);
        if (fullPatient) {
          console.log("✅ ConfirmPatientInfo - Full patient fetched:", fullPatient);
          setPatientData((prev) => ({
            ...prev,
            ...locationUser,
            ...fullPatient,
            isDropIn: prev?.isDropIn || locationUser?.isDropIn || false,
            encounterId:
              prev?.encounterId ||
              locationUser?.id ||
              locationUser?.encounterId,
            encounterDateTime:
              prev?.encounterDateTime || locationUser?.encounterDateTime,
            studyIds: prev?.studyIds || locationUser?.studyIds || [],
            consents: prev?.consents || locationUser?.consents || [],
          }));
        }
      } catch (error) {
        console.error("❌ ConfirmPatientInfo - Failed to fetch full patient:", error);
      }
    };

    fetchFullPatient();
  }, [locationUser, patientData?.patientId, patientData?.email, setPatientData]);
 
  // ── Sync location state into context (first render only) ───────────────────
  useEffect(() => {
    if (!locationUser) return;
    if (patientId === locationUser.patientId) return;
    setPatientData((prev) => ({ ...prev, ...locationUser }));
  }, [locationUser, patientId, setPatientData]);

  useEffect(() => {
    setContactInfo({
      email: patientEmail,
      mobilePhoneNumber: formatPhoneNumber(patientMobilePhoneNumber),
    });
    setContactErrors({});
  }, [patientId, patientEmail, patientMobilePhoneNumber]);
 
  // ── Set scheduled time + initialize check-in time once ────────────────────
  useEffect(() => {
    if (!patientData) return;
 
    const encounterDateTime = patientData.encounterDateTime;
    if (encounterDateTime) {
      setScheduledTime(formatScheduledAppointmentDateTime(encounterDateTime));
    }
 
    if (!checkInTimeSetRef.current) {
      checkInTimeSetRef.current = true;
      const initialIso = patientData.checkInTime || patientData.checkInDateTime || nowIso();
      setCheckInTime(isoToChicagoDateTimeLocal(initialIso));
      updatePatientData({ checkInTime: initialIso });
    }
  }, [patientData, updatePatientData]);
 


  // ── Load consent eligibility status ──────────────────────────────────────────
  const loadStudiesAndConsents = useCallback(async () => {
    if (!patientData?.patientId) {
      setSignedStudies([]);
      setIsLoading(false);
      return;
    }

    try {
      // Use the same eligibility API as other pages for consistency
      const eligibility = await fetchAPI(
        `biobank/consent/eligibility/preview?patientId=${patientData.patientId}`,
        "GET"
      );
      console.log("🔍 Fetched consent eligibility:", eligibility);

      // Get encounter study IDs
      const encounterStudyIds =
        patientData.studyIds || patientData.selectedStudies || [];

      // Build effective study IDs set from backend response
      const effectiveStudies = eligibility.effectiveActiveStudies || [];
      const effectiveStudyIdsSet = new Set(
        effectiveStudies.map(s => s.irbStudy)
      );

      // Map encounter studies to status
      const studiesWithStatus = encounterStudyIds.map(irbStudy => {
        const studyInfo = effectiveStudies.find(s => s.irbStudy === irbStudy);
        return {
          irbStudy: irbStudy,
          studyName: studyInfo?.studyName || studyInfo?.studyShortName || irbStudy,
          status: effectiveStudyIdsSet.has(irbStudy) ? "active" : "incomplete",
        };
      });

      setSignedStudies(studiesWithStatus);
    } catch (error) {
      console.error("Error loading consent eligibility:", error);
      setSignedStudies([]);
    } finally {
      setIsLoading(false);
    }
  }, [patientData]);

  useEffect(() => {
    if (patientData?.patientId) {
      loadStudiesAndConsents();
    }
  }, [loadStudiesAndConsents, patientData?.patientId]);
  // ── Handlers ──────────────────────────────────────────────────────────────
 
  const handleCheckInTimeChange = (event) => {
    const nextValue = event.target.value;
    setCheckInTime(nextValue);
    const nextIso = chicagoDateTimeLocalToIso(nextValue);
    if (nextIso) {
      updatePatientData({ checkInTime: nextIso });
    }
  };

  const handleContactInfoChange = (field) => (event) => {
    const value =
      field === "mobilePhoneNumber"
        ? formatPhoneNumber(event.target.value)
        : event.target.value;

    setContactInfo((prev) => ({ ...prev, [field]: value }));
    setContactErrors((prev) => ({ ...prev, [field]: "", submit: "" }));
  };

  const validateContactInfo = (nextContactInfo) => {
    const errors = {};
    const email = nextContactInfo.email.trim();
    const mobilePhoneNumber = nextContactInfo.mobilePhoneNumber.trim();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;

    if (!email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      errors.email = "Invalid email format";
    }

    if (!mobilePhoneNumber) {
      errors.mobilePhoneNumber = "Phone number is required";
    } else if (!phoneRegex.test(mobilePhoneNumber)) {
      errors.mobilePhoneNumber = "Phone must be 10 digits in format (XXX) XXX-XXXX";
    }

    return errors;
  };

  const preparePatientContactInfo = async () => {
    const normalizedContactInfo = {
      email: contactInfo.email.trim(),
      mobilePhoneNumber: contactInfo.mobilePhoneNumber.trim(),
    };
    const errors = validateContactInfo(normalizedContactInfo);

    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return null;
    }

    const updatedPatientData = {
      ...patientData,
      ...normalizedContactInfo,
    };

    if (patientData?.patientId) {
      try {
        setIsSavingContact(true);
        await updatePatient(patientData.patientId, normalizedContactInfo);
      } catch (error) {
        console.error("Failed to save participant contact info:", error);
        setContactErrors({
          submit: "Failed to save email and phone number. Please try again.",
        });
        return null;
      } finally {
        setIsSavingContact(false);
      }
    }

    updatePatientData(normalizedContactInfo);

    return updatedPatientData;
  };
 
  const handleContinue = async () => {
    const currentPatientData = await preparePatientContactInfo();
    if (!currentPatientData) return;

    await handleSkipToNextPage(currentPatientData);
  };
 
  const handleSkipToNextPage = async (currentPatientData = patientData) => {
    try {
      let encounterId = currentPatientData?.encounterId;
 
      if (!encounterId) {
        const activeStudyIds = signedStudies
          .filter((s) => s.status === "active")
          .map((s) => s.irbStudy);

        const encounterData = {
          encounterId: uuidv4(),
          patientId: currentPatientData.patientId,
          encounterDateTime:
            currentPatientData.encounterDateTime || nowIso(),
          encounterType: ENCOUNTER_TYPE.REGULAR,
          checkInStatus: CHECK_IN_STATUS.NOT_CHECKED_IN,
          scheduledStudyIds: activeStudyIds,
        };
        console.log("Creating encounter:", encounterData);
        const createdEncounter = await createEncounter(encounterData);
        encounterId = createdEncounter.encounterId || createdEncounter.id;
        console.log("✅ Encounter created:", encounterId);
 
        updatePatientData({ encounterId, selectedStudies: activeStudyIds });
      }
 
      const generalInfoComplete = currentPatientData?.generalInfoComplete || false;
      const nextStep = generalInfoComplete
        ? BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_FAMILY
        : BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_GENERAL;

      navigate(BIOBANK_SUB_ROUTES.PARTICIPANT_CHECKIN_WIZARD_CONSENT, {
        state: {
          user: currentPatientData,
          patient: currentPatientData,
          encounterId,
          nextStep,
        },
      });
    } catch (error) {
      console.error("❌ Error in skip navigation:", error);
    }
  };

  const handleBack = () => {
    navigate(BIOBANK_ROUTES.PARTICIPANT_CHECKIN);
  };
 
  const handleChipClick = () => {
    console.log("Chip clicked");
  };
 
  // ── Derived state ─────────────────────────────────────────────────────────
 
  const activeStudies = signedStudies.filter((s) => s.status === "active");
  const incompleteStudies = signedStudies.filter((s) => s.status === "incomplete");
  const hasNoStudies = signedStudies.length === 0;
  const scheduledDateKey = formatDateInAppTimeZone(patientData?.encounterDateTime);
  const checkInDateKey = checkInTime ? checkInTime.slice(0, 10) : "";
  const showCheckInDateMismatch =
    Boolean(scheduledDateKey && checkInDateKey && scheduledDateKey !== checkInDateKey);
 
  // ── Loading guard ─────────────────────────────────────────────────────────
 
  if (!patientData) {
    return (
      <FocusedFlowPage title="Confirm Participant Information" maxWidth={760}>
        <Typography sx={{ textAlign: "center", mt: 4 }}>Loading...</Typography>
      </FocusedFlowPage>
    );
  }
 
  // ── Render ────────────────────────────────────────────────────────────────
 
  return (
    <FocusedFlowPage
      title="Confirm Participant Information"
      subtitle="Please review and verify the following information with the participant to be sure you are checking-in the correct participant."
      onClose={() => navigate(BIOBANK_ROUTES.PARTICIPANT_CHECKIN)}
      maxWidth={760}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>,
        <Button
          key="continue"
          variant="contained"
          onClick={handleContinue}
          disabled={isLoading || isSavingContact}
          endIcon={<ArrowForward />}
        >
          {isLoading || isSavingContact ? "Loading..." : "Continue"}
        </Button>,
      ]}
    >
      <ParticipantConfirmationSummary
        scheduledAppointmentTime={scheduledTime}
        fullName={[patientData.firstName, patientData.lastName].filter(Boolean).join(" ")}
        firstName={patientData.firstName}
        lastName={patientData.lastName}
        contactOrder="phone-email"
        emailContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              required
              size="small"
              type="email"
              value={contactInfo.email}
              onChange={handleContactInfoChange("email")}
              error={!!contactErrors.email}
              inputProps={{ "aria-label": "Email" }}
              sx={{ ...compactInputSx, width: 220, maxWidth: "100%" }}
            />
            {contactErrors.email && (
              <Typography sx={{ color: "error.main", fontSize: "13px", lineHeight: 1.2 }}>
                {contactErrors.email}
              </Typography>
            )}
          </Box>
        }
        birthDate={formatBirthDate(patientData.birthDate)}
        mobilePhoneNumberContent={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              required
              size="small"
              value={contactInfo.mobilePhoneNumber}
              onChange={handleContactInfoChange("mobilePhoneNumber")}
              error={!!contactErrors.mobilePhoneNumber}
              placeholder="(XXX) XXX-XXXX"
              inputProps={{ "aria-label": "Phone Number" }}
              sx={{ ...compactInputSx, width: 220, maxWidth: "100%" }}
            />
            {contactErrors.mobilePhoneNumber && (
              <Typography sx={{ color: "error.main", fontSize: "13px", lineHeight: 1.2 }}>
                {contactErrors.mobilePhoneNumber}
              </Typography>
            )}
          </Box>
        }
        hasNoStudies={hasNoStudies}
        activeStudies={activeStudies}
        incompleteStudies={incompleteStudies}
        onChipClick={handleChipClick}
      >
        {/* Check-In Time — editable datetime picker, defaults to now */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "260px minmax(0, 1fr)" },
            alignItems: "center",
            columnGap: 2,
            rowGap: 0.5,
            mb: "16px",
          }}
        >
          <Typography
            component="label"
            htmlFor="check-in-date-time"
            sx={{ fontWeight: 700 }}
          >
            Check-In Time:
          </Typography>
          <Box>
            <TextField
              id="check-in-date-time"
              type="datetime-local"
              size="small"
              value={checkInTime}
              onChange={handleCheckInTimeChange}
              inputProps={{ "aria-label": "Check-In Time" }}
              sx={{ ...compactInputSx, width: 220, maxWidth: "100%" }}
            />
            {showCheckInDateMismatch && (
              <Typography sx={{ color: "warning.main", fontSize: "13px", lineHeight: 1.3, mt: 0.5 }}>
                Check-in date is different from the scheduled appointment date. Please confirm the check-in time again.
              </Typography>
            )}
          </Box>
        </Box>
      </ParticipantConfirmationSummary>

      {contactErrors.submit && (
        <Typography sx={{ color: "#d32f2f", textAlign: "center", mt: 2 }}>
          {contactErrors.submit}
        </Typography>
      )}
    </FocusedFlowPage>
  );
};
 
export default ConfirmPatientCheckInInfo;
